/**
 * Surveys List component for managing user's surveys
 * Access: admins and internal BluePill team members (@blue-pill.ai)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, FileText, Calendar, Eye, ExternalLink } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import { getUserSurveys, Survey, surveyIdToShareId, getSurveyGroups, SurveyGroup } from '../../utils/surveyApi';
import { SURVEY_STATES } from '../../constants/surveyConstants';
import { UI_TEXT } from '../../config/ui-text';
import { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { useAuth } from '../../context/AuthContext';

const SurveysList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isInternalUser } = useAuth();

  // Redirect users without survey access (admins + internal team) to home
  if (!isAdmin && !isInternalUser) {
    return <Navigate to="/" replace />;
  }
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [surveyGroups, setSurveyGroups] = useState<SurveyGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [surveysResult, groupsResult] = await Promise.all([
          getUserSurveys(),
          getSurveyGroups().catch(() => ({ groups: [] })),
        ]);
        const groupSurveyIds = new Set(groupsResult.groups.flatMap(g => g.survey_ids));
        setSurveys(surveysResult.surveys.filter(s => !groupSurveyIds.has(s.id)));
        setSurveyGroups(groupsResult.groups);
      } catch (error) {
        console.error('Failed to load surveys:', error);
        setError(UI_TEXT.HUMAN_SURVEY.LIST.ERRORS.FAILED_TO_LOAD);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Combined and sorted list of surveys and groups
  const sortedItems = useMemo(() => {
    const surveyItems = surveys.map(s => ({ type: 'survey' as const, data: s, created_at: s.created_at }));
    const groupItems = surveyGroups.map(g => ({ type: 'group' as const, data: g, created_at: g.created_at }));
    return [...surveyItems, ...groupItems].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [surveys, surveyGroups]);

  const getStateColor = (state: string) => {
    switch (state) {
      case SURVEY_STATES.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SURVEY_STATES.DRAFT:
        return 'bg-yellow-100 text-yellow-800';
      case SURVEY_STATES.COMPLETED:
        return 'bg-blue-100 text-blue-800';
      case SURVEY_STATES.ARCHIVED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSurveyDisplayName = (survey: Survey) => {
    if (survey.name && survey.name.trim()) {
      return survey.name;
    }
    // Generate a name using the survey ID
    return `Survey #${survey.id}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.LIST.LOADING.LOADING}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{UI_TEXT.HUMAN_SURVEY.LIST.TITLES.HUMAN_SURVEYS}</h1>
            <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.LIST.DESCRIPTIONS.MAIN}</p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/surveys/new')}
            icon={<Plus className="w-4 h-4" />}
          >
            {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.CREATE_SURVEY}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Surveys and Groups Grid - Combined and Sorted by Creation Date */}
        {sortedItems.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{UI_TEXT.HUMAN_SURVEY.LIST.TITLES.NO_SURVEYS}</h3>
            <p className="text-gray-600 mb-6">{UI_TEXT.HUMAN_SURVEY.LIST.DESCRIPTIONS.NO_SURVEYS}</p>
            <Button
              variant="primary"
              onClick={() => navigate('/surveys/new')}
              icon={<Plus className="w-4 h-4" />}
            >
              {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.CREATE_FIRST_SURVEY}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedItems.map((item) => {
              if (item.type === 'group') {
                const group = item.data as SurveyGroup;
                return (
                  <div
                    key={`group-${group.id}`}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{group.name}</h3>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          active
                        </span>
                      </div>
                    </div>

                    {/* Group tags with colors */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {group.group_labels.map((label: string, idx: number) => {
                        const colors = getGroupColor(idx);
                        return (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.CREATED} {formatDate(group.created_at)}
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(`${import.meta.env.VITE_API_URL || ''}/api/survey-groups/distribute/${group.id}`, '_blank', 'noopener,noreferrer')}
                        icon={<ExternalLink className="w-3.5 h-3.5" />}
                      >
                        Take Survey
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/survey-groups/${group.id}`)}
                      >
                        {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.VIEW_DETAILS}
                      </Button>
                    </div>
                  </div>
                );
              } else {
                const survey = item.data as Survey;
                return (
                  <div
                    key={survey.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {getSurveyDisplayName(survey)}
                        </h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStateColor(survey.state)}`}
                          >
                            {survey.state}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.CREATED} {formatDate(survey.created_at)}
                      </div>
                      {survey.published_at && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Eye className="w-4 h-4 mr-2" />
                          {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.PUBLISHED} {formatDate(survey.published_at)}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/surveys/${survey.id}`)}
                      >
                        {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.VIEW_DETAILS}
                      </Button>
                      {survey.state === SURVEY_STATES.ACTIVE && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => window.open(`/surveys/${surveyIdToShareId(survey.id)}/take`, '_blank', 'noopener,noreferrer')}
                        >
                          {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.TAKE_SURVEY}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/surveys/${surveyIdToShareId(survey.id)}/take?preview=true`, '_blank', 'noopener,noreferrer')}
                        icon={<Eye className="w-3.5 h-3.5" />}
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default SurveysList;